import { useCallback, useEffect, useRef, useState } from "react";
import type { Account, Candlestick, Deposit, Leaderboard, Market, NewsItem, Order, OrderbookDepth, Ticker, TpsStats, TradingPair } from "@/lib/wsTypes";
import { parseAccount, parseCandlestick, parseDeposit, parseLeaderboard, parseMarket, parseNewsItem, parseOrder, parseTicker, parseTpsStats } from "@/lib/wsTypes";

/**
 * Live market-data client for the channel streaming API (see
 * docs/streaming-api.md). Connects to `/ws`, reads the plaintext `welcome`
 * (no handshake, no app-layer encryption -- TLS covers that in prod), then
 * subscribes to channels: public `markets` + `stats`, and per-market
 * `orderbook:<m>` + `candles:<m>:<interval>`, plus private (authed) `account`
 * + `orders` + `deposits`. The server replies with a `snapshot` per channel,
 * then `update` deltas. Order/deposit submission still goes over the encrypted
 * HTTP path -- this socket is read-only.
 */

/** Candle granularities the UI renders (chart timeframes + 24h stats). */
const CANDLE_GRANULARITIES = ["1s", "1m", "1h", "1d"] as const;

/** Placeholder pair for a depth row before its market's metadata has arrived;
 * depth consumers key by `pairKey` and read price/amounts, never `pair`. */
const UNKNOWN_PAIR: TradingPair = { base: { tag: "Ada" }, quote: { tag: "Ada" } };

function initialLedgerView() {
  return {
    account: null as Account | null,
    deposits: {} as Record<string, Deposit>,
    markets: {} as Record<string, Market>,
    depths: {} as Record<string, OrderbookDepth[]>,
    // One ticker per market, subscribed for ALL markets (unlike orderbooks,
    // which are per-viewed-market): a ticker is one row, and a price for every
    // market lets balances be valued without visiting each one.
    tickers: {} as Record<string, Ticker>,
    orders: {} as Record<string, Order>,
    candlesticks: {} as Record<string, Candlestick>,
    tpsStats: null as TpsStats | null,
    tradingEnabled: false,
    // Competition: one ranked leaderboard per prize category, and the
    // already-published news wire (kept sorted by publish time).
    leaderboards: {} as Record<string, Leaderboard>,
    news: [] as NewsItem[],
  };
}

type LedgerView = ReturnType<typeof initialLedgerView>;
/** market (canonical key) -> price string -> aggregated level. */
type Book = Map<string, Map<string, OrderbookDepth>>;

/** The per-market channels a viewer needs for the market it's looking at. */
function marketChannels(marketKey: string): string[] {
  return [`orderbook:${marketKey}`, ...CANDLE_GRANULARITIES.map((g) => `candles:${marketKey}:${g}`)];
}

/** Channels that carry account-private data and require an auth signature. */
const PRIVATE_CHANNELS: string[] = ["account", "orders", "deposits"];

/** Signs the WS auth message, returning the COSE key + signature the server
 * verifies. Produced by the account owner (the ephemeral/delegated key). */
export type WsAuthSigner = (
  message: string,
) => Promise<{ coseKey: string; coseSignature: string }>;

export function useWebSocket(
  url: string,
  accountHash: string | null,
  signAuth?: WsAuthSigner,
  onAuthError?: (code: string) => void,
) {
  const ws = useRef<WebSocket | null>(null);
  // The single market this client currently watches (orderbook + candles). Only
  // this market is subscribed -- not all of them -- so a page load costs ~7
  // snapshots, not ~27, which keeps a connect storm cheap. Survives reconnects.
  const watchedMarket = useRef<string | null>(null);
  // Latest signer + auth-error handler, read via refs so a changing wallet
  // identity doesn't tear down the socket (the effect keys on url+accountHash).
  const signAuthRef = useRef(signAuth);
  signAuthRef.current = signAuth;
  const onAuthErrorRef = useRef(onAuthError);
  onAuthErrorRef.current = onAuthError;
  // Re-attempts the authed private subscribe for the live connection; the
  // watchdog calls it until the server accepts (null while disconnected).
  const retryPrivateRef = useRef<(() => void) | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ledgerView, setLedgerView] = useState(initialLedgerView);

  useEffect(() => {
    let stopped = false;
    // A half-open socket (TCP alive but no data -- server restarted mid-stream,
    // NAT timeout, a stale server-side view) never fires onclose, so the
    // reconnect path never runs and the UI freezes on its last snapshot. Track
    // the last frame time and force a reconnect if the feed goes quiet; the
    // server sends `{"event":"heartbeat"}` every 10s even on an idle ledger,
    // so genuine silence this long means a dead feed.
    let lastFrameAt = Date.now();

    function connect() {
      const socket = new WebSocket(url);
      socket.binaryType = "arraybuffer";
      ws.current = socket;

      let welcomed = false;
      // Set once the server accepts the authed private subscribe. Until then the
      // watchdog re-attempts it, covering a delegated key that is not registered
      // on the ledger yet (a freshly deposited account, pre first trade).
      let privateReady = false;
      // The aggregated book that backs `depths`, rebuilt fresh on each connect
      // (no cross-reconnect phantoms).
      const book: Book = new Map();
      // Ticker channels already subscribed on THIS connection; the markets
      // snapshot re-arrives per connect, re-filling this from scratch.
      const subscribedTickers = new Set<string>();
      // Per-market orderbook seq chain (gap detection), reset with the book.
      const bookSeq = new Map<string, string>();

      const sub = (channels: string[]) => {
        if (socket.readyState !== WebSocket.OPEN) return;
        socket.send(JSON.stringify({ op: "subscribe", channels }));
      };

      // Subscribe the private channels with a fresh CIP-8 signature over the
      // exact message the server verifies (`SUGAR-RUSH-WS-AUTH-V1:<hex
      // accountHash>:<nonce>`). Signed with the local ephemeral (delegated) key,
      // so no wallet prompt. A guest (no signer / no wallet) owns no private
      // data and never subscribes these.
      const subPrivate = async () => {
        const signer = signAuthRef.current;
        // Subscribe only after `welcome`, and never for a guest (no signer).
        if (!welcomed || !signer || !accountHash || privateReady) return;
        const nonce = Date.now();
        let signed: { coseKey: string; coseSignature: string };
        try {
          signed = await signer(`SUGAR-RUSH-WS-AUTH-V1:${accountHash}:${nonce}`);
        } catch (e) {
          console.error("[ws] failed to sign private-subscribe auth", e);
          return;
        }
        if (socket.readyState !== WebSocket.OPEN || privateReady) return;
        socket.send(
          JSON.stringify({
            op: "subscribe",
            channels: PRIVATE_CHANNELS,
            auth: { account: accountHash, nonce, ...signed },
          }),
        );
      };
      retryPrivateRef.current = () => {
        void subPrivate();
      };

      socket.onopen = () => {
        setConnected(true);
        setError(null);
        lastFrameAt = Date.now();
      };
      socket.onclose = () => {
        setConnected(false);
        retryPrivateRef.current = null;
        if (!stopped) setTimeout(connect, 3_000);
      };
      socket.onerror = (e) => {
        console.error("[ws] error", e);
        setError("WebSocket connection failed.");
      };

      socket.onmessage = (e) => {
        lastFrameAt = Date.now();
        const text = typeof e.data === "string" ? e.data : new TextDecoder().decode(e.data as ArrayBuffer);
        let parsed: any;
        try {
          parsed = JSON.parse(text);
        } catch {
          return;
        }

        // A batch of live updates arrives as a JSON array of data messages; a
        // snapshot or control frame arrives as a single object.
        if (Array.isArray(parsed)) {
          applyDataMessages(parsed);
          return;
        }
        // A data frame carries `channel` + `type` (and is tagged
        // `event:"data"`); route it before the control branch so the tag
        // isn't mistaken for a control event.
        if (parsed && typeof parsed.channel === "string" && typeof parsed.type === "string") {
          applyDataMessages([parsed]);
          return;
        }
        if (parsed && typeof parsed.event === "string") {
          if (parsed.event === "welcome" && !welcomed) {
            welcomed = true;
            // The server replays a full snapshot per channel on (re)subscribe,
            // and a snapshot only re-inserts what is currently live -- it never
            // deletes. Drop the volatile collections here so a row deleted on
            // the ledger during a disconnect gap can't linger as a phantom.
            // Keep `markets` (re-sent on the markets snapshot; also stops
            // `selectedMarket` flickering null on every reconnect).
            book.clear();
            setLedgerView((prev) => ({
              ...initialLedgerView(),
              markets: prev.markets,
              // `tradingEnabled` is a scalar flag, not a volatile collection --
              // resetting it to false on every reconnect made it flap
              // true->false->true, which re-fired the one-shot reveal splash
              // (App.tsx). The `stats` snapshot re-sent below reconciles it.
              tradingEnabled: prev.tradingEnabled,
            }));
            sub(["markets", "stats", "leaderboards", "news"]);
            void subPrivate();
            // Re-subscribe the market we're watching (if any) on this fresh socket.
            if (watchedMarket.current) sub(marketChannels(watchedMarket.current));
          } else if (parsed.event === "subscribed") {
            // Any private-channel ack means auth succeeded; stop re-attempting.
            if (PRIVATE_CHANNELS.includes(parsed.channel)) privateReady = true;
          } else if (parsed.event === "error") {
            console.error("[ws] server error", parsed);
            // An auth rejection on a private channel means our delegated key is
            // not registered for this account. Hand it to the owner to recover
            // (re-delegate); the watchdog keeps re-attempting subPrivate.
            const code: string = parsed.code ?? "";
            if (code.startsWith("auth_") && PRIVATE_CHANNELS.includes(parsed.channel)) {
              onAuthErrorRef.current?.(code);
            }
          }
          return;
        }
      };

      /** Fold a batch of `{channel,type,data}` messages into one state update. */
      function applyDataMessages(messages: any[]) {
        // Verify orderbook seq chains before touching state: a `prevSeq` that
        // doesn't extend the last applied frame means a delta was missed --
        // force a reconnect (fresh snapshot) instead of rendering a book we
        // know has drifted.
        for (const msg of messages) {
          if (
            typeof msg?.channel === "string" &&
            msg.channel.startsWith("orderbook:") &&
            msg.data?.market &&
            !advanceBookSeq(bookSeq, msg.data, msg.type)
          ) {
            console.error(`[ws] orderbook seq gap on ${msg.data.market} -- forcing reconnect`);
            try {
              socket.close();
            } catch {
              /* onclose drives the reconnect */
            }
            return;
          }
        }
        setLedgerView((prev) => {
          const next: LedgerView = {
            ...prev,
            deposits: prev.deposits,
            markets: prev.markets,
            depths: prev.depths,
            orders: prev.orders,
            candlesticks: prev.candlesticks,
          };
          const dirtyBooks = new Set<string>();

          for (const msg of messages) {
            if (!msg || typeof msg.channel !== "string") continue;
            const { channel, type, data } = msg;

            if (channel === "markets") {
              next.markets = { ...next.markets };
              for (const raw of data ?? []) {
                const m = parseMarket(raw);
                next.markets[m.key] = m;
              }
              // Every market gets its ticker followed (cheap: one row each).
              const fresh = Object.keys(next.markets).filter((k) => !subscribedTickers.has(k));
              if (fresh.length > 0) {
                for (const k of fresh) subscribedTickers.add(k);
                sub(fresh.map((k) => `ticker:${k}`));
              }
            } else if (channel === "stats") {
              if (data) {
                next.tradingEnabled = !!data.tradingEnabled;
                next.tpsStats = data.tps ? parseTpsStats(data.tps) : next.tpsStats;
              }
            } else if (channel === "account") {
              if (data) next.account = parseAccount(data);
            } else if (channel === "orders") {
              next.orders = { ...next.orders };
              const list = type === "snapshot" ? (data ?? []) : [data];
              for (const raw of list) {
                if (!raw) continue;
                const o = parseOrder(raw);
                next.orders[o.id.toString()] = o;
              }
            } else if (channel === "deposits") {
              next.deposits = { ...next.deposits };
              const list = type === "snapshot" ? (data ?? []) : [data];
              for (const raw of list) {
                if (!raw) continue;
                const d = parseDeposit(raw);
                next.deposits[d.requestId.toString()] = d;
              }
            } else if (channel === "leaderboards") {
              next.leaderboards = { ...next.leaderboards };
              const list = type === "snapshot" ? (data ?? []) : [data];
              for (const raw of list) {
                if (!raw) continue;
                const l = parseLeaderboard(raw);
                next.leaderboards[l.category] = l;
              }
            } else if (channel === "news") {
              // Snapshot and live items can overlap; dedupe by (time, headline).
              const list = type === "snapshot" ? (data ?? []) : [data];
              const key = (n: NewsItem) => `${n.timeMs}:${n.headline}`;
              const seen = new Set(next.news.map(key));
              const fresh = (list ?? [])
                .filter(Boolean)
                .map(parseNewsItem)
                .filter((n: NewsItem) => !seen.has(key(n)));
              if (fresh.length > 0) {
                next.news = [...next.news, ...fresh].sort((a, b) =>
                  a.timeMs < b.timeMs ? -1 : a.timeMs > b.timeMs ? 1 : 0,
                );
              }
            } else if (channel.startsWith("candles:")) {
              next.candlesticks = { ...next.candlesticks };
              const list = type === "snapshot" ? (data ?? []) : [data];
              for (const raw of list) {
                if (!raw) continue;
                const c = parseCandlestick(raw);
                next.candlesticks[c.key] = c;
              }
            } else if (channel.startsWith("ticker:")) {
              if (data?.market) {
                const t = parseTicker(data);
                next.tickers = { ...next.tickers, [t.market]: t };
              }
            } else if (channel.startsWith("orderbook:")) {
              applyOrderbook(book, data, type);
              if (data?.market) dirtyBooks.add(data.market);
            }
          }

          // Rebuild the affected markets' depth arrays from the aggregated book.
          if (dirtyBooks.size > 0) {
            next.depths = { ...next.depths };
            for (const market of dirtyBooks) {
              const levels = book.get(market);
              const pair = next.markets[market]?.tradingPair ?? UNKNOWN_PAIR;
              next.depths[market] = levels
                ? [...levels.values()].map((l) => ({ ...l, pair }))
                : [];
            }
          }
          return next;
        });
      }
    }

    connect();

    const watchdog = setInterval(() => {
      if (stopped) return;
      // Retry the authed private subscribe until the server accepts it (no-op
      // once ready, or for a guest). Covers a delegated key registered just
      // after connect, so private data appears without a full reconnect.
      retryPrivateRef.current?.();
      if (Date.now() - lastFrameAt > 15_000 && ws.current) {
        console.warn("[ws] no frames for >15s -- forcing reconnect");
        lastFrameAt = Date.now(); // avoid re-firing during the reconnect gap
        try {
          ws.current.close();
        } catch {
          /* onclose drives the reconnect */
        }
      }
    }, 5_000);

    return () => {
      stopped = true;
      clearInterval(watchdog);
      ws.current?.close();
    };
  }, [url, accountHash]);

  const send = useCallback((msg: string) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(msg);
    }
  }, []);

  /** Point this client's orderbook + candle feed at `marketKey` (the market the
   * user is viewing). Unsubscribes the previous market and subscribes the new
   * one; idempotent; re-applied automatically across reconnects. */
  const watchMarket = useCallback((marketKey: string | null) => {
    const prev = watchedMarket.current;
    if (prev === marketKey) return;
    watchedMarket.current = marketKey;
    const sock = ws.current;
    if (!sock || sock.readyState !== WebSocket.OPEN) return;
    if (prev) sock.send(JSON.stringify({ op: "unsubscribe", channels: marketChannels(prev) }));
    if (marketKey) sock.send(JSON.stringify({ op: "subscribe", channels: marketChannels(marketKey) }));
  }, []);

  return { ledgerView, connected, error, send, watchMarket };
}

/** Advance a market's orderbook seq chain: a `snapshot` (re)starts it at its
 * `seq`; an `update` must carry `prevSeq` equal to the last applied `seq`.
 * Returns `false` when a frame was missed (the caller resyncs). */
function advanceBookSeq(seqs: Map<string, string>, data: any, type: string): boolean {
  const market = String(data.market);
  if (type === "snapshot") {
    if (data.seq != null) seqs.set(market, String(data.seq));
    else seqs.delete(market);
    return true;
  }
  if (data.seq == null || data.prevSeq == null) return true;
  const last = seqs.get(market);
  if (last != null && String(data.prevSeq) !== last) return false;
  seqs.set(market, String(data.seq));
  return true;
}

/** Apply an orderbook `snapshot` (full book, reset first) or `update` (the
 * levels that changed; a `0` bid and ask means the level was removed) into
 * `book`. Each depth level carries both sides at its price. */
function applyOrderbook(book: Book, data: any, type: string): void {
  const market: string | undefined = data?.market;
  if (!market) return;
  let levels = book.get(market);
  if (type === "snapshot" || !levels) {
    levels = new Map();
    book.set(market, levels);
  }

  const at = (priceStr: string): OrderbookDepth => {
    let lvl = levels!.get(priceStr);
    if (!lvl) {
      lvl = {
        key: priceStr,
        pairKey: market,
        pair: UNKNOWN_PAIR,
        price: BigInt(priceStr),
        bidAmount: 0n,
        askAmount: 0n,
      };
      levels!.set(priceStr, lvl);
    }
    return lvl;
  };

  for (const [priceStr, qtyStr] of data.bids ?? []) at(priceStr).bidAmount = BigInt(qtyStr);
  for (const [priceStr, qtyStr] of data.asks ?? []) at(priceStr).askAmount = BigInt(qtyStr);
  // Drop levels emptied on both sides.
  for (const [priceStr, lvl] of levels) {
    if (lvl.bidAmount === 0n && lvl.askAmount === 0n) levels.delete(priceStr);
  }
}
